import { Fragment } from 'react';
import {
    Skeleton,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { formatPrice } from '../../../functions';
import { formatInventoryActivityTime } from '../../../components/inventory/inventoryActivityLogShared';
import {
    formatBusinessLineDisplay,
    formatInventoryPartTypeLabel,
    formatInventoryUnitLabel,
    formatStockAdjustmentRoDisplay,
    INVENTORY_CATEGORY_OPTIONS,
} from '../../../utils/inventoryConstants';
import type { InventoryReportingAdjustmentRow } from '../../../types/reporting';
import { AUDIT_INVENTORY_LABELS } from '../auditingInventoryConfig';

const EMPTY = '—';
const LIST_SEP = '; ';

function displayText(value: string | null | undefined): string {
    if (value == null || value === '') return EMPTY;
    return value;
}

function formatNamedList(items: Array<{ name: string }> | null | undefined): string {
    if (!items?.length) return EMPTY;
    return items.map((item) => item.name).filter(Boolean).join(LIST_SEP) || EMPTY;
}

function formatCategory(category: InventoryReportingAdjustmentRow['category']): string {
    return INVENTORY_CATEGORY_OPTIONS.find((opt) => opt.value === category)?.label ?? category;
}

function formatSignedQty(value: number): string {
    if (value > 0) return `+${value.toLocaleString()}`;
    return value.toLocaleString();
}

function signedColor(value: number): string {
    if (value > 0) return 'green.600';
    if (value < 0) return 'red.600';
    return 'gray.700';
}

function typeQty(
    row: InventoryReportingAdjustmentRow,
    types: InventoryReportingAdjustmentRow['type'] | InventoryReportingAdjustmentRow['type'][],
): string {
    const allowed = Array.isArray(types) ? types : [types];
    if (!allowed.includes(row.type)) return EMPTY;
    return Math.abs(row.quantity).toLocaleString();
}

function cellValue(row: InventoryReportingAdjustmentRow, key: string): React.ReactNode {
    switch (key) {
        case 'name':
            return displayText(row.name);
        case 'manufacturerPartNumber':
            return displayText(row.manufacturerPartNumber);
        case 'category':
            return formatCategory(row.category);
        case 'partType':
            return row.partType ? formatInventoryPartTypeLabel(row.partType) : EMPTY;
        case 'unit':
            return row.unit ? formatInventoryUnitLabel(row.unit) : EMPTY;
        case 'roNumber': {
            const ro = formatStockAdjustmentRoDisplay(row.roNumber);
            return ro || EMPTY;
        }
        case 'note':
            return displayText(row.note);
        case 'bins':
            return formatNamedList(row.bins);
        case 'tags':
            return formatNamedList(row.tags);
        case 'locationTitle':
            return displayText(row.locationTitle);
        case 'supplierName':
            return displayText(row.supplierName);
        case 'businessLine': {
            const bl = formatBusinessLineDisplay(row.businessLine);
            return bl || EMPTY;
        }
        case 'timestamp':
            return row.timestamp ? formatInventoryActivityTime(row.timestamp) : EMPTY;
        case 'quantity':
            return (
                <Text as="span" color={signedColor(row.quantity)} fontWeight="medium">
                    {formatSignedQty(row.quantity)}
                </Text>
            );
        case 'inStockQuantityAfter':
            return row.inStockQuantityAfter != null
                ? row.inStockQuantityAfter.toLocaleString()
                : EMPTY;
        case 'orderedAndInitialQuantity':
            return typeQty(row, ['order', 'initial']);
        case 'usedQuantity':
            return typeQty(row, 'usage');
        case 'soldQuantity':
            return typeQty(row, 'sale');
        case 'targetQuantity':
            return row.targetQuantity != null ? row.targetQuantity.toLocaleString() : EMPTY;
        case 'reorderQuantity':
            return row.reorderQuantity != null ? row.reorderQuantity.toLocaleString() : EMPTY;
        case 'unitCost':
            return row.unitCost != null ? formatPrice(row.unitCost) : EMPTY;
        case 'lineCost':
            return (
                <Text as="span" color={signedColor(row.lineCost)} fontWeight="medium">
                    {formatPrice(row.lineCost)}
                </Text>
            );
        case 'adjustmentType':
            return displayText(row.type);
        default:
            return EMPTY;
    }
}

export interface AuditingInventoryTableProps {
    rows: InventoryReportingAdjustmentRow[];
    visibleColumnKeys: string[];
    isLoading: boolean;
    tableDetailMode?: boolean;
}

const AuditingInventoryTable = ({
    rows,
    visibleColumnKeys,
    isLoading,
}: AuditingInventoryTableProps) => {
    if (isLoading) {
        return (
            <VStack spacing={2} p={4}>
                {Array.from({ length: 10 }).map((_, i) => (
                    <Skeleton key={i} height="32px" width="100%" />
                ))}
            </VStack>
        );
    }

    return (
        <Table variant="simple" size="sm">
            <Thead position="sticky" top={0} zIndex={1}>
                <Tr>
                    {visibleColumnKeys.map((key) => (
                        <Th key={key} whiteSpace="nowrap">
                            {AUDIT_INVENTORY_LABELS[key] ?? key}
                        </Th>
                    ))}
                </Tr>
            </Thead>
            <Tbody>
                {rows.length === 0 ? (
                    <Tr>
                        <Td colSpan={Math.max(visibleColumnKeys.length, 1)} textAlign="center" py={8}>
                            <Text color="gray.500" fontSize="sm">
                                No stock adjustments found
                            </Text>
                        </Td>
                    </Tr>
                ) : (
                    rows.map((row) => (
                        <Tr key={row._id} _hover={{ bg: 'gray.50' }}>
                            {visibleColumnKeys.map((key) => (
                                <Td key={key} whiteSpace="nowrap" fontSize="sm">
                                    <Fragment>{cellValue(row, key)}</Fragment>
                                </Td>
                            ))}
                        </Tr>
                    ))
                )}
            </Tbody>
        </Table>
    );
};

export default AuditingInventoryTable;
